import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Copy, CheckCircle2 } from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type CreateResult = {
  customerId: string;
  brandId: string;
  auditId: string;
  asinCount: number;
  inviteUrl: string;
};

export default function AdminProspectNew() {
  return (
    <AdminGate>
      <AdminLayout>
        <Inner />
      </AdminLayout>
    </AdminGate>
  );
}

function Inner() {
  const [email, setEmail] = useState("");
  const [brandName, setBrandName] = useState("");
  const [asins, setAsins] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateResult | null>(null);
  const [copied, setCopied] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await api<CreateResult>("/admin/prospects", {
      method: "POST",
      body: JSON.stringify({ email: email.trim(), brandName: brandName.trim(), asins }),
    });
    setSubmitting(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Failed to create prospect.");
      return;
    }
    setResult(res.data);
  }

  async function copyLink() {
    if (!result) return;
    await navigator.clipboard.writeText(result.inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-10">
      <div className="mb-6">
        <Link to="/admin/prospects" className="text-sm text-slate-500 hover:text-slate-900">
          ← Prospects
        </Link>
      </div>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
        New prospect prefill
      </h1>
      <p className="mt-2 text-sm text-slate-600">
        Creates the customer + brand + monitored ASINs, kicks off scrape and classify
        immediately, returns a magic-link URL the prospect uses to sign in. They land
        on a populated dashboard with their flagged reviews already shown.
      </p>

      {!result && (
        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Prospect email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@brandname.com"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Brand name
            </label>
            <input
              type="text"
              required
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="Acme Coffee"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              ASINs <span className="text-slate-500">(one per line, or paste from a spreadsheet)</span>
            </label>
            <textarea
              required
              rows={10}
              value={asins}
              onChange={(e) => setAsins(e.target.value)}
              placeholder={"B0CFPJYX7P\nB0DMTNDHMY\nB0F2A1B3C4"}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
            <p className="mt-1 text-xs text-slate-500">
              Trial cap is 200 ASINs. Both bare ASINs and full Amazon URLs work.
            </p>
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Creating…" : "Create prefill →"}
          </Button>
        </form>
      )}

      {result && (
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-center gap-2 text-emerald-900">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-semibold">
              Prefilled — {result.asinCount} ASINs queued for scrape
            </span>
          </div>
          <p className="mt-2 text-sm text-emerald-900">
            Share this magic link with the prospect. It expires in 14 days and is
            single-use. They'll land on a dashboard with their flagged reviews.
          </p>
          <div className="mt-4 flex items-center gap-2">
            <input
              readOnly
              value={result.inviteUrl}
              className="flex-1 rounded-lg border border-emerald-300 bg-white px-3 py-2 font-mono text-xs text-slate-900"
              onClick={(e) => (e.currentTarget as HTMLInputElement).select()}
            />
            <Button type="button" onClick={copyLink} variant="secondary">
              <Copy className="h-4 w-4" />
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to={`/admin/customers/${result.customerId}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Open customer →
            </Link>
            <button
              type="button"
              onClick={() => {
                setResult(null);
                setEmail("");
                setBrandName("");
                setAsins("");
              }}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Create another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
